import { type Component, computed, defineAsyncComponent, markRaw, ref } from 'vue'
import { defineStore } from 'pinia'
import { useAccountFlagsStore } from '@/store/pinia/AccountFlagsStore.ts'
import { GenericObject } from '@/types/GlobalTypes.ts'
import getNavigationObject from '@/composables/navigationRouteObjects.ts'
import { userRole } from '@/lib/AuthenticatorPlugin.ts'
import { useCheckUser } from '@/composables/users/checkUser.ts'
import {
  ExclusionRules,
  useWhitelabelExclusionRules,
} from '@/composables/useWhitelabelExclusionRules.ts'
import { FeatureList, useLDFlag } from '@/lib/ldClient.ts'

export const useNavBarStore = defineStore('NavBarStore', () => {
  const accountFlagsStore = useAccountFlagsStore()
  const showAncillaryLinks = computed(() => !routerComponents.value.includes('reporting'))
  const { isUser, isAdmin } = useCheckUser()
  const { hasExclusionRule } = useWhitelabelExclusionRules()

  const accountFlags = computed(() => accountFlagsStore.getAccountFlags)
  const ldFlags = ref<GenericObject<boolean>>({})
  const navsOpenState = ref<GenericObject<boolean>>({})
  const routerComponents = ref<string[]>([])

  //Mod 2.1
  const disableSalarySacrificeContributions = computed(
    () =>
      (!accountFlagsStore.getFlag('salarySacrificeContributions', 'questions_completed') &&
        isAdmin()) ||
      ldFlags.value.disabledModule2,
  )
  //Mod 2.2
  const disableLumpSumConcessionalContributions = computed(
    () =>
      (!accountFlagsStore.getFlag('lumpSumConcessionalContributions', 'questions_completed') &&
        isAdmin()) ||
      ldFlags.value.disabledModule2,
  )
  //Mod 2.3
  const disableLumpSumNonConcessionalContributions = computed(
    () =>
      (!accountFlagsStore.getFlag('lumpSumNonConcessionalContributions', 'questions_completed') &&
        isAdmin()) ||
      ldFlags.value.disabledModule2 ||
      ldFlags.value.disabledModuleNonConcessionalContributions,
  )
  //Mod Insurance
  const disablePersonalInsurance = computed(
    () =>
      (!accountFlagsStore.getFlag('personalInsurance', 'questions_completed') && isAdmin()) ||
      ldFlags.value.disabledInsurance,
  )
  const disablePersonalInsuranceExpenses = computed(
    () =>
      !accountFlagsStore.getFlag('personalInsurance', 'questions_completed') ||
      isAdmin() ||
      ldFlags.value.disabledInsurance,
  )

  const hideRetirementPlan = computed(() => {
    return (
      hasExclusionRule(ExclusionRules.RETIREMENT_PLAN) ||
      !useLDFlag(FeatureList.ENABLE_RETIREMENT_PLAN, false).value
    )
  })

  const setNavOpenState = (navHeading: string, value: boolean) => {
    navsOpenState.value[navHeading] = value
  }
  const setLDFlags = (payload: GenericObject<boolean>) => {
    ldFlags.value = payload
  }

  const setRouterComponents = (payload: string[]) => {
    routerComponents.value = payload
  }

  const disableTabsWhenClientHasNotCompletedOnboarding = computed(() => {
    return !accountFlags.value.genericQuestions.questions_completed || ldFlags.value.disabledModule2
  })

  /**
   * Sidemenu Nav Array - Computed for proper reactivity
   *
   */
  const routes = computed(() => [
    {
      heading: 'Dashboard',
      icon: markRaw(
        defineAsyncComponent(() => import('@/components/icons/LineGraph.vue')),
      ) as Component,
      routerName: 'clientDashboard',
      hide: isAdmin(),
    },
    {
      heading: 'Debt',
      icon: markRaw(
        defineAsyncComponent(() => import('@/components/icons/BombIcon.vue')),
      ) as Component,
      open: false,
      primaryStatus: ['debtPlan'],
      content: [
        {
          label: 'Debt plan',
          routerName: 'debtPlan',
        },
      ],
      hide: hasExclusionRule(ExclusionRules.DEBT_MODULE),
    },
    {
      heading: 'Super',
      icon: markRaw(
        defineAsyncComponent(() => import('@/components/SVGS/SuperSun.vue')),
      ) as Component,
      open: false,
      primaryStatus: [
        'superInvestments',
        'salarySacrificeContributions',
        'lumpSumConcessionalContributions',
        'lumpSumNonConcessionalContributions',
      ],
      content: [
        {
          label: 'Investment options inside super',
          routerName: 'superInvestments',
        },
        {
          label: 'Contributions from your salary',
          routerName: 'salarySacrificeContributions',
          disabled: disableSalarySacrificeContributions.value,
        },
        {
          label: 'Tax deductible contributions from savings',
          routerName: 'lumpSumConcessionalContributions',
          disabled: disableLumpSumConcessionalContributions.value,
        },
        {
          label: 'Non-deductible contributions from savings',
          routerName: 'lumpSumNonConcessionalContributions',
          disabled: disableLumpSumNonConcessionalContributions.value,
        },
      ],
    },
    {
      heading: 'Insurance',
      icon: markRaw(
        defineAsyncComponent(() => import('@/components/icons/InsuranceColouredIcon.vue')),
      ) as Component,
      open: false,
      disabled: ldFlags.value.disabledInsurance,
      primaryStatus: 'personalInsurance',
      content: [
        {
          label: 'Personal insurance',
          routerName: 'personalInsurance',
          disabled: disablePersonalInsurance.value,
        },
        {
          label: 'My expenses',
          routerName: 'insuranceExpenses',
          disabled: disablePersonalInsuranceExpenses.value,
        },
      ],
    },
    {
      heading: 'Retirement',
      icon: markRaw(
        defineAsyncComponent(() => import('@/components/icons/RetirementIcon.vue')),
      ) as Component,
      open: false,
      primaryStatus: ['retirementPlan'],
      content: [{ label: 'Retirement plan', routerName: 'retirementPlan' }],
      hide: hideRetirementPlan.value,
    },
    {
      heading: 'Profile',
      icon: markRaw(
        defineAsyncComponent(() => import('@/components/icons/ProfileIcon.vue')),
      ) as Component,
      open: false,
      content: [
        {
          label: 'Account details',
          routerName: 'clientDetails',
        },
        {
          label: 'Account status',
          routerName: 'guidanceMemberSummary',
          hide: isUser(),
        },
        {
          label: 'Financial summary',
          routerName: 'clientFinancialSummary',
          hide: disableTabsWhenClientHasNotCompletedOnboarding.value,
        },
      ],
    },
    // Hiding it, not supposed to show yet.
    {
      heading: 'Support',
      icon: markRaw(
        defineAsyncComponent(() => import('@/components/icons/QuestionBoxIcon.vue')),
      ) as Component,
      open: false,
      hide: true || userRole.value === 'superadmin' || userRole.value === 'callcentre',
      content: [
        {
          label: 'Contact us',
          routerName: 'clientContactUs',
        },
      ],
    },
  ])

  // {
  //   heading: 'Downsizing',
  //     icon: markRaw(
  //   defineAsyncComponent(() => import('@/components/icons/InsuranceColouredIcon.vue')),
  // ) as Component,
  //   open: false,
  //   disabled: ldFlags.value.disabledDownsizing ?? false,
  //   primaryStatus: 'personalInsurance',
  //   content: [
  //   {
  //     label: 'Downsizing',
  //     routerName: 'downsizing',
  //   },
  // ],
  // },

  /**
   * Navigation routes
   * @returns {Array<SideNavAccordionsType>}
   * @description This is the main navigation object that is used to render the side navigation bar.
   */
  const routesArray = computed(() => {
    if (routerComponents.value.includes('reporting')) {
      return getNavigationObject('reporting')
    } else {
      return routes.value
    }
  })

  return {
    showAncillaryLinks,
    getNavRoutes: computed(() => routesArray.value),
    getNavsOpenState: computed(() => navsOpenState.value),
    setLDFlags,
    setNavOpenState,
    setRouterComponents,
    getRouteArray: routes,
  }
})
